import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { Auth } from 'aws-amplify';
import Modal from '../Modal';
import { useFormik } from 'formik';
import { string, object } from 'yup';
import QRCode from 'qrcode.react';
import Input from '../form/Input';

const smsPhoneSchema = object().shape({
    sms_phone: string().required('Phone number is required')
});

export const mfaTypes = [
    {
        name: 'Use a mobile app',
        desc: 'Security codes will be generated by an authenticator app',
        key: 'TOTP',
        pType: 'SOFTWARE_TOKEN_MFA'
    },
    {
        name: 'Use text messages',
        desc: 'Security codes will be sent to your mobile phone',
        key: 'SMS',
        pType: 'SMS_MFA'
    }
];

export const getMFAMethodName = (key) => {
    switch (key) {
        case 'SOFTWARE_TOKEN_MFA':
        case 'TOTP':
            return 'Authenticator App';
        case 'SMS_MFA':
        case 'SMS':
            return 'SMS';
        default:
            return 'Unknown';
    }
};

const SetupMFA = ({ currentUser, visible = false, onCancel = () => {}, onSuccess = () => {} }) => {
    const [status, setStatus] = useState('select');
    const [error, setError] = useState(null);
    const [totpCode, setTotpCode] = useState('');
    let successTimeout = null;

    const handleSuccess = () => {
        successTimeout && clearTimeout(successTimeout);
        onSuccess();
        setTimeout(() => {
            reset();
        }, 2000);
    };

    const handleSaveSelection = async (values, { setSubmitting }) => {
        const mfaType = mfaTypes.find((t) => t.pType === values.mfaMethod || t.key === values.mfaMethod);

        if (mfaType.key === 'TOTP') {
            // Cognito keeps a disabled software token's secret valid indefinitely — it's only
            // replaced once a new one is associated *and verified*. Always route through that
            // flow here so re-enabling never silently reactivates an old, possibly
            // lost/compromised authenticator without the user proving current access.
            setSubmitting(false);
            setStatus('setupTOTP');
            return;
        }

        try {
            const user = await Auth.currentAuthenticatedUser();
            await Auth.setPreferredMFA(user, mfaType.key);

            setSubmitting(false);
            setStatus('success');
            setTimeout(() => {
                handleSuccess();
            }, 3000);
        } catch (err) {
            const { message = '' } = err;
            setSubmitting(false);
            console.error(err);
            if (/phone.?number|sms/i.test(message)) {
                setStatus('setupSMS');
            } else {
                setStatus('error');
                setError(err);
            }
        }
    };

    const setupTotp = async () => {
        try {
            let user = await Auth.currentAuthenticatedUser();
            let data = await Auth.setupTOTP(user);
            const code = `otpauth://totp/AWSCognito:${user.username}?secret=${data}&issuer=AWSCognito`;
            setTotpCode(code);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        if (status === 'setupTOTP') {
            setupTotp();
        }
    }, [status]);

    const selectForm = useFormik({
        initialValues: {
            mfaMethod: currentUser?.preferredMFA || ''
        },
        onSubmit: handleSaveSelection,
        enableReinitialize: true
    });

    const verifyTOTP = async (values, { setSubmitting }) => {
        try {
            let user = await Auth.currentAuthenticatedUser();
            await Auth.verifyTotpToken(user, values.verify_code);
            await Auth.setPreferredMFA(user, 'TOTP');
            setStatus('success');
        } catch (err) {
            console.error(err);
        }
    };

    const verifyTOTPForm = useFormik({
        initialValues: {
            verify_code: ''
        },
        onSubmit: verifyTOTP
    });

    const reset = () => {
        setStatus('select');
        setTotpCode('');
        setError(null);
    };

    const handleSelectType = (type) => {
        selectForm.setFieldValue('mfaMethod', type);
    };

    const setSmsForm = useFormik({
        initialValues: {
            sms_phone: ''
        },
        validationSchema: smsPhoneSchema,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                const user = await Auth.currentAuthenticatedUser();
                await Auth.updateUserAttributes(user, { phone_number: values.sms_phone });
                setStatus('verifySMS');
            } catch (err) {
                console.error(err);
                setError(err);
            } finally {
                setSubmitting(false);
            }
        }
    });

    const verifySmsForm = useFormik({
        initialValues: {
            code: ''
        },
        onSubmit: async (values) => {
            try {
                let user = await Auth.currentAuthenticatedUser();
                await Auth.verifyTotpToken(user, values.verify_code);
                await Auth.setPreferredMFA(user, 'SMS');
                setStatus('success');
            } catch (err) {
                console.error(err);
            }
        }
    });

    return (
        <Modal
            label="Setup account MFA"
            visible={visible}
            onClose={() => {
                handleSuccess();
            }}
        >
            {status === 'select' && (
                <div className="txt-center">
                    <h3>Select MFA Type</h3>
                    <form onSubmit={selectForm.handleSubmit}>
                        <div className="btn-group mb-8">
                            {mfaTypes.map((type) => (
                                <button
                                    key={type.key}
                                    className={classnames('btn btn-outline-primary', {
                                        active:
                                            selectForm.values.mfaMethod === type.pType ||
                                            selectForm.values.mfaMethod === type.key
                                    })}
                                    onClick={() => handleSelectType(type.key)}
                                    type="button"
                                >
                                    <strong>{type.name}</strong>
                                    <br />
                                    <small>{type.desc}</small>
                                </button>
                            ))}
                        </div>
                        <div>
                            <button className="btn btn-s">Cancel</button>
                            <button className="btn btn-primary">Next</button>
                        </div>
                    </form>
                </div>
            )}
            {status === 'setupTOTP' && (
                <div>
                    <QRCode value={totpCode} />
                    <form onSubmit={verifyTOTPForm.handleSubmit}>
                        <Input
                            id="verify_code"
                            label="Enter Security Code"
                            value={verifyTOTPForm.values.verify_code}
                            onChange={verifyTOTPForm.handleChange}
                        />
                        <button className="btn btn-primary">Verify Security Token</button>
                    </form>
                </div>
            )}
            {status === 'setupSMS' && (
                <div>
                    <p>Enter your mobile phone number</p>
                    <p>
                        We’ll send a security code to this phone number whenever you sign in to AudioEngine Gateway or
                        link a new device.
                    </p>
                    <form onSubmit={setSmsForm.handleSubmit}>
                        <Input
                            id="sms_phone"
                            label="Phone Number"
                            value={setSmsForm.values.sms_phone}
                            error={setSmsForm.errors.sms_phone}
                            touched={setSmsForm.touched.sms_phone}
                            onChange={setSmsForm.handleChange}
                            onBlur={setSmsForm.handleBlur}
                            hideLabel
                        />
                        {error && <p className="mt-2 error-text">{error.message}</p>}
                        <button className="btn btn-primary">Next</button>
                    </form>
                </div>
            )}
            {status === 'verifySMS' && (
                <div>
                    <p>
                        We sent a security code to {setSmsForm.values.sms_phone}. Enter it below to verify your phone
                        number
                    </p>
                    <form onSubmit={verifySmsForm.handleSubmit}>
                        <Input
                            id="verify_code"
                            label="Enter Security Code"
                            value={verifySmsForm.values.verify_code}
                            onChange={verifySmsForm.handleChange}
                        />
                        <button className="btn btn-primary">Verify Security Token</button>
                    </form>
                </div>
            )}
            {status === 'error' && (
                <div className="p-8 txt-center">
                    <p>Something went wrong setting up MFA{error?.message ? `: ${error.message}` : '.'}</p>
                    <p>
                        <button className="btn btn-secondary" onClick={reset}>
                            Try Again
                        </button>
                    </p>
                </div>
            )}
            {status === 'success' && (
                <div className="p-8 txt-center">
                    <p>
                        Success! Your Two-factor Verification is setup using{' '}
                        {getMFAMethodName(selectForm.values.mfaMethod)}
                    </p>
                    <p>
                        <button className="btn btn-secondary" onClick={handleSuccess}>
                            Close
                        </button>
                    </p>
                </div>
            )}
        </Modal>
    );
};

export default SetupMFA;
